/**
 * Lead tracking helper.
 * Sends external form submissions to the CRM via the External Tracking events endpoint.
 * Fire-and-forget — never blocks the form UX.
 */

type StandardTrackingFieldKey = string;
type RegisteredCustomFieldId = string;
type TrackingCustomField = { value?: unknown; label: string };
type TrackingFileField = { file?: File; label: string };
type TrackingImageDataField = { dataUrl?: string; label: string };

export const TRACKING_IDS = {
  trackingId: "tk_01a4e829270a4be8912b9033fd44825b",
  locationId: "k94aHcRKI8Yx46SYfgfo",
  projectId: "1792357746068722743",
} as const;

export const postTrackingEvent = (
  trackingPayload: Record<string, unknown> & {
    formData: Record<StandardTrackingFieldKey, unknown>;
    formLabels: Record<StandardTrackingFieldKey, string>;
  },
  options: {
    customFields?: Record<
      RegisteredCustomFieldId,
      TrackingCustomField | undefined
    >;
    fileFields?: Record<RegisteredCustomFieldId, TrackingFileField>;
    imageDataFields?: Record<RegisteredCustomFieldId, TrackingImageDataField>;
  } = {},
) => {
  const { customFields = {}, fileFields = {}, imageDataFields = {} } = options;
  const eventPayload = {
    ...trackingPayload,
    formData: { ...trackingPayload.formData },
    formLabels: { ...trackingPayload.formLabels },
  };
  const body = new FormData();

  for (const [key, field] of Object.entries(customFields)) {
    if (!field || field.value === undefined) continue;
    eventPayload.formData[key] = field.value;
    eventPayload.formLabels[key] = field.label;
  }

  for (const [key, field] of Object.entries(imageDataFields)) {
    const dataUrl = field.dataUrl;
    if (!dataUrl) continue;
    if (!dataUrl.startsWith("data:image/")) {
      throw new Error("Image data field must be a data:image/* base64 string");
    }
    eventPayload.formData[key] = dataUrl;
    eventPayload.formLabels[key] = field.label;
  }

  for (const [key, field] of Object.entries(fileFields)) {
    const file = field.file;
    if (!file) continue;
    if (file.size > 50 * 1024 * 1024) {
      throw new Error("File must be 50 MB or smaller");
    }
    eventPayload.formData[key] = {
      filename: file.name,
      size: file.size,
      type: file.type || "application/octet-stream",
    };
    eventPayload.formLabels[key] = field.label;
    body.append(key, file, file.name);
  }

  for (const key of Object.keys(eventPayload.formData)) {
    eventPayload.formLabels[key] ||= key;
  }

  body.append("event", JSON.stringify(eventPayload));

  // keepalive: true ensures the request survives page navigation
  // (e.g. redirect to a thank-you page) so the CRM always receives it.
  return fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
    method: "POST",
    headers: {
      version: "2021-07-28",
    },
    body,
    keepalive: true,
  }).catch(() => {});
};

/**
 * Reuse the published site's analytics session (set by /__vibe/analytics.js)
 * so the submission joins the visit's page views; fall back to a fresh id
 * when it is absent or unreadable (document.cookie throws in a sandboxed
 * iframe).
 */
const getTrackingSessionId = () => {
  try {
    const sid = document.cookie.match(
      /(?:^|;\s*)__vibe_sid=([0-9a-f-]{36})(?:;|$)/,
    )?.[1];
    if (sid) return sid;
  } catch {
    /* ignore — sandboxed iframe can't read cookies */
  }
  return crypto.randomUUID();
};

/** Build the common tracking payload envelope shared by every form. */
export const buildTrackingPayload = (formId: string, formName: string) => {
  // Capture OpenAI Ads tracking parameters (campaign_id, ad_group_id,
  // ad_id, ad_account_id, oppref) that were attached to the landing URL
  // when the visitor clicked an ad. Persisted in sessionStorage by the
  // capture script in index.html so they survive client-side navigation.
  let oaiAdParams: Record<string, string> = {};
  try {
    const stored = sessionStorage.getItem("oai_ad_params");
    if (stored) oaiAdParams = JSON.parse(stored);
  } catch {
    /* ignore parse errors */
  }

  return {
    type: "external_form_submission" as const,
    timestamp: Date.now(),
    formId,
    formData: {} as Record<string, unknown>,
    formLabels: {} as Record<string, string>,
    url: window.location.href,
    title: document.title,
    path: window.location.pathname,
    userAgent: navigator.userAgent,
    trackingId: TRACKING_IDS.trackingId,
    locationId: TRACKING_IDS.locationId,
    projectId: TRACKING_IDS.projectId,
    sessionId: getTrackingSessionId(),
    properties: {
      deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
        ? "mobile"
        : "desktop",
      source: "ai_studio",
      projectId: TRACKING_IDS.projectId,
      formName,
      ...(Object.keys(oaiAdParams).length > 0 ? { oaiAdParams } : {}),
    },
  };
};
